import { MantineColor } from '@mantine/core';
import { Badge } from 'src/design/atoms/Badge';
import { Group } from 'src/design/atoms/Group';
import { Popover } from 'src/design/molecules/Popover';
import { Stack } from 'src/design/atoms/Stack';
import { Text } from 'src/design/atoms/Text';
import { Menu } from 'src/design/molecules/Menu';
import { TextInput } from 'src/design/atoms/TextInput';
import { DatePickerInput } from 'src/design/atoms/DatePickerInput';
import { IconSquare, IconSquareCheck } from '@tabler/icons-react';
import moment from 'moment';
import React from 'react';
import { TopicIssueDto, UserCoreDto } from 'src/api/FiretrolApiClient';
import { useUpdateMeetingIssue } from 'src/api/queries/meetings/useMeetingIssueMutations';
import { showSuccessToast, formatDateTime } from 'src/utils';
import useDebounce from 'src/hooks/useDebounce';
import { usePendingEdit } from 'src/routes/Jobs/MeetingMinutes/MeetingForm/pendingEdits';
import { BasicActionIcon } from 'src/design/atoms/BasicActionIcon';
import { AssignToPicker } from 'src/routes/Jobs/MeetingMinutes/MeetingForm/components/Topic/components/AssignToPicker';
import { OptionMenu } from 'src/routes/Jobs/MeetingMinutes/MeetingForm/components/Topic/components/OptionMenu';

const PRIORITY_COLORS: Record<string, MantineColor> = {
  Low: 'gray',
  Medium: 'yellow',
  High: 'red',
};

interface TopicIssueProps {
  issue: TopicIssueDto;
  jobId: number;
  meetingId: string;
  selected: boolean;
  onAddCommentClick: () => void;
  onDeleteIssue: () => void;
  onIssueChange: (issue: TopicIssueDto) => void;
  onAddIssue: () => void;
}

export const TopicIssue: React.FC<TopicIssueProps> = ({
  issue,
  jobId,
  meetingId,
  onIssueChange,
  onAddIssue,
  onDeleteIssue,
  onAddCommentClick,
  selected,
}) => {
  const [title, setTitle] = React.useState(issue.title);
  const debouncedTitle = useDebounce(title, 700);
  usePendingEdit(`issue-title-${issue.id}`, title !== issue.title);

  const updateIssue = useUpdateMeetingIssue(
    jobId,
    meetingId,
    issue.topicId!,
    issue.id,
  );

  // The write lives here because the mutation is scoped per issue; the parent
  // topic only mirrors the change back into the form tree.
  const applyIssueChange = (next: TopicIssueDto) => {
    onIssueChange(next);
    updateIssue.mutate(next);
  };

  React.useEffect(() => {
    applyIssueChange({
      ...issue,
      title,
    });
  }, [debouncedTitle]);

  const onAssigneeChange = (user: UserCoreDto | null) => {
    applyIssueChange({
      ...issue,
      assignee: user ?? undefined,
    });
  };

  const onPriorityChange = (priority: 'Low' | 'Medium' | 'High') => {
    applyIssueChange({
      ...issue,
      priority,
    });
  };

  const toggleComplete = () => {
    const newVal = !issue.complete;
    applyIssueChange({
      ...issue,
      complete: !issue.complete,
    });
    if (newVal) {
      showSuccessToast(`Issue Resolved!`);
    }
  };

  const handleKeyDown = (e: any) => {
    if (e.key === 'Enter') {
      onAddIssue();
    }
  };

  const onDueDateChange = (date: Date | null) => {
    applyIssueChange({
      ...issue,
      dueDate: date,
    });
  };

  const isOverdue =
    issue.dueDate &&
    moment(issue.dueDate).isBefore(moment()) &&
    !issue.complete;

  return (
    <Stack gap="xs">
      <Group gap="xs" align="center">
        <BasicActionIcon
          variant="subtle"
          onClick={toggleComplete}
          color="blue"
          size="lg"
        >
          {issue.complete ? (
            <IconSquareCheck size={20} />
          ) : (
            <IconSquare size={20} />
          )}
        </BasicActionIcon>

        <TextInput
          placeholder="Description..."
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          onKeyDown={handleKeyDown}
          style={{ flex: 1 }}
          styles={{
            input: {
              textDecoration: issue.complete ? 'line-through' : 'none',
              color: issue.complete ? '#666' : 'inherit',
            },
          }}
        />

        <AssignToPicker
          initialValue={issue.assignee}
          onChange={onAssigneeChange}
        />

        <OptionMenu
          onAddComment={onAddCommentClick}
          onDeleteIssue={onDeleteIssue}
        />
      </Group>

      <Group gap="xs" wrap="wrap">
        {issue.assignee ? (
          <Badge
            variant="light"
            leftSection={
              <Text size="xs" fw={500}>
                {issue.assignee.firstName?.slice(0, 1)}
              </Text>
            }
          >
            Assigned to {issue.assignee.fullName}
          </Badge>
        ) : (
          <Badge variant="light" color="gray">
            Unassigned
          </Badge>
        )}

        <Badge
          variant="light"
          onClick={onAddCommentClick}
          style={{ cursor: 'pointer' }}
          leftSection={
            (issue.comments?.length ?? 0) > 0 ? (
              <Text size="xs" fw={500}>
                {issue.comments![
                  issue.comments!.length - 1
                ].author?.firstName?.slice(0, 1)}
              </Text>
            ) : undefined
          }
        >
          {issue.comments?.length ?? 0} comment
          {(issue.comments?.length ?? 0) === 1 ? '' : 's'}
        </Badge>

        <Popover position="left" withArrow>
          <Popover.Target>
            <Badge
              variant="light"
              color={isOverdue ? 'red' : 'gray'}
              style={{ cursor: 'pointer' }}
            >
              {issue.dueDate
                ? `Due ${formatDateTime(
                    issue.dueDate,
                    'MM/DD/YYYY [at] h:mm a',
                  )}`
                : 'No Due Date'}
            </Badge>
          </Popover.Target>
          <Popover.Dropdown>
            <DatePickerInput
              value={issue.dueDate ? moment(issue.dueDate).toDate() : null}
              onChange={onDueDateChange}
              placeholder="Select due date"
              clearable
            />
          </Popover.Dropdown>
        </Popover>

        <Menu>
          <Menu.Target>
            <Badge
              variant="light"
              color={PRIORITY_COLORS[issue.priority]}
              style={{ cursor: 'pointer' }}
            >
              {issue.priority} Priority
            </Badge>
          </Menu.Target>
          <Menu.Dropdown>
            <Menu.Label>Issue Priority</Menu.Label>
            <Menu.Item
              onClick={() => onPriorityChange('Low')}
              leftSection={issue.priority === 'Low' ? '✓' : undefined}
            >
              Low
            </Menu.Item>
            <Menu.Item
              onClick={() => onPriorityChange('Medium')}
              leftSection={issue.priority === 'Medium' ? '✓' : undefined}
            >
              Medium
            </Menu.Item>
            <Menu.Item
              onClick={() => onPriorityChange('High')}
              leftSection={issue.priority === 'High' ? '✓' : undefined}
            >
              High
            </Menu.Item>
          </Menu.Dropdown>
        </Menu>
      </Group>
    </Stack>
  );
};
